import { useCallback, useEffect, useMemo, useReducer } from 'react';

import { endOfDay, parseISO, startOfYear } from 'date-fns';

import {
    dashboardData, getHistoricalTopProducts, getOrdersByYear
} from '../../../../actions/admin/dashboard';
import {
    buildDashboardModel, buildFilteredData, buildTopProductsRangeData,
    createDashboardInitialModel, getAverageTicketView,
    getDashboardPeriodRange, mergeOrdersByYear
} from '../../../../helpers/admin/dashboard/dashboardHelper';

const today = new Date();
const initialFilterStartRange = startOfYear(today);

const initialState = {
    ...createDashboardInitialModel(today),
    reportingToday: today,
    defaultRange: {
        startDate: initialFilterStartRange,
        endDate: today
    },
    historicalTopProducts: [],
    historicalTopProductsLoading: true,
    dialogRange: false
};

const dashboardReducer = (state, action) => {
    switch (action.type) {
        case 'LOAD_DASHBOARD':
        case 'SELECT_AVERAGE_YEAR':
        case 'APPLY_DATE_RANGE':
            return { ...state, ...action.payload };
        case 'SET_DATE_DIALOG':
            return { ...state, dialogRange: action.open };
        case 'LOAD_HISTORICAL_TOP_PRODUCTS':
            return {
                ...state,
                historicalTopProducts: action.products,
                historicalTopProductsLoading: false
            };
        default:
            return state;
    }
};

const useDashboardData = () => {
    const [state, dispatch] = useReducer(dashboardReducer, initialState);

    const availableYears = useMemo(() => {
        if (state.averageTicket.length === 0)
            return [{ year: today.getFullYear(), isLoaded: false }];

        return state.averageTicket.map(({ year, isLoaded }) => ({
            year,
            isLoaded
        }));
    }, [state.averageTicket]);

    const loadMissingYears = useCallback(async (years, sourceState) => {
        const groupedOrders = await getOrdersByYear(years);

        if (!groupedOrders)
            return null;

        return mergeOrdersByYear(
            groupedOrders,
            sourceState.customers,
            sourceState.allOrders,
            sourceState.averageTicket
        );
    }, []);

    const loadDashboard = useCallback(async () => {
        const response = await dashboardData();

        if (!response)
            return;

        const {
            orders, today: serverDate, reportingDate, ...info
        } = response;
        const parsedServerDate = parseISO(reportingDate || serverDate);
        const filterStartRange = startOfYear(parsedServerDate);
        let model = buildDashboardModel(
            filterStartRange, orders, parsedServerDate, info
        );
        let selectedTicket = model.averageTicket.find(
            ticket => ticket.year === model.year
        );

        if (selectedTicket && !selectedTicket.isLoaded) {
            const loaded = await loadMissingYears([model.year], model);

            if (loaded) {
                model = { ...model, ...loaded };
                selectedTicket = model.averageTicket.find(
                    ticket => ticket.year === model.year
                );
            }
        }

        dispatch({
            type: 'LOAD_DASHBOARD',
            payload: {
                ...model,
                reportingToday: parsedServerDate,
                defaultRange: {
                    startDate: filterStartRange,
                    endDate: endOfDay(parsedServerDate)
                },
                currentAverageTicket: getAverageTicketView(selectedTicket)
            }
        });
    }, [loadMissingYears]);

    useEffect(() => {
        loadDashboard();
    }, [loadDashboard]);

    useEffect(() => {
        const loadHistoricalTopProducts = async() => {
            const products = await getHistoricalTopProducts();
            dispatch({
                type: 'LOAD_HISTORICAL_TOP_PRODUCTS',
                products
            });
        };

        loadHistoricalTopProducts();
    }, []);

    const selectAverageTicketYear = useCallback(async (selectedYear) => {
        let selectedTicket = state.averageTicket.find(
            ticket => ticket.year === selectedYear
        );
        let loaded = null;

        if (selectedTicket && !selectedTicket.isLoaded) {
            loaded = await loadMissingYears([selectedYear], state);

            if (!loaded)
                return false;

            selectedTicket = loaded.averageTicket.find(
                ticket => ticket.year === selectedYear
            );
        }

        dispatch({
            type: 'SELECT_AVERAGE_YEAR',
            payload: {
                ...(loaded ?? {}),
                year: selectedYear,
                currentAverageTicket: getAverageTicketView(selectedTicket)
            }
        });

        return true;
    }, [loadMissingYears, state]);

    const applyDateRange = useCallback(async (startDate, selectedEndDate) => {
        const endDate = endOfDay(selectedEndDate);
        const startYear = startDate.getFullYear();
        const endYear = endDate.getFullYear();
        const years = Array.from(
            { length: endYear - startYear + 1 },
            (_, index) => startYear + index
        );
        const missingYears = years.filter(
            year => !state.allOrders[year]?.isLoaded
        );
        let loaded = null;
        let ordersByYear = state.allOrders;

        if (missingYears.length > 0) {
            loaded = await loadMissingYears(missingYears, state);

            if (!loaded)
                return false;

            ordersByYear = loaded.allOrders;
        }

        const filteredData = buildFilteredData(
            ordersByYear,
            startDate,
            endDate,
            state.topProductsData,
            state.dateRanges
        );
        const selectedTicket = (loaded?.averageTicket ?? state.averageTicket)
            .find(ticket => ticket.year === state.year);

        dispatch({
            type: 'APPLY_DATE_RANGE',
            payload: {
                ...(loaded ?? {}),
                ...filteredData,
                defaultRange: { startDate, endDate },
                dialogRange: false,
                currentAverageTicket: getAverageTicketView(selectedTicket)
            }
        });

        return true;
    }, [loadMissingYears, state]);

    const ensureAnnualTopProducts = useCallback(async () => {
        const { startDate, endDate } = getDashboardPeriodRange(
            'annual', state.reportingToday
        );
        const years = Array.from(
            { length: endDate.getFullYear() - startDate.getFullYear() + 1 },
            (_, index) => startDate.getFullYear() + index
        );
        const missingYears = years.filter(
            year => !state.allOrders[year]?.isLoaded
        );
        let loaded = null;
        let ordersByYear = state.allOrders;

        if (missingYears.length > 0) {
            loaded = await loadMissingYears(missingYears, state);
            if (!loaded)
                return false;
            ordersByYear = loaded.allOrders;
        }

        dispatch({
            type: 'LOAD_DASHBOARD',
            payload: {
                ...(loaded ?? {}),
                topProductsData: {
                    ...state.topProductsData,
                    annualData: buildTopProductsRangeData(
                        ordersByYear, startDate, endDate
                    )
                }
            }
        });

        return true;
    }, [loadMissingYears, state]);

    const setDateDialog = useCallback(open => {
        dispatch({ type: 'SET_DATE_DIALOG', open });
    }, []);

    return {
        today: state.reportingToday,
        filterStartRange: state.defaultRange.startDate,
        sales: {
            today: state.todayTotalData,
            week: state.weekTotalData,
            month: state.monthTotalData,
            quarter: state.quarterTotalData,
            yearToDate: state.yearToDateTotalData,
            filtered: state.filteredTotalData
        },
        topProducts: {
            data: state.topProductsData,
            ensureAnnualData: ensureAnnualTopProducts,
            historical: state.historicalTopProducts,
            historicalLoading: state.historicalTopProductsLoading
        },
        newCustomers: {
            firstSemester: state.firstSemester,
            secondSemester: state.secondSemester,
            total: state.totalNewClients,
            customers: state.customers
        },
        stateSales: {
            country: state.countrySalesData,
            topStates: state.topStatesData
        },
        regularCustomers: {
            monthly: state.fqtMonthlyData,
            quarterly: state.fqtQuarterlyData,
            yearToDate: state.fqtYearToDateData
        },
        dateFilter: {
            ranges: state.dateRanges,
            range: state.defaultRange,
            dialogOpen: state.dialogRange,
            openDialog: () => setDateDialog(true),
            closeDialog: () => setDateDialog(false),
            applyRange: applyDateRange
        },
        averageTicket: {
            availableYears,
            data: state.averageTicket,
            current: state.currentAverageTicket,
            year: state.year,
            selectYear: selectAverageTicketYear,
            orders: state.allOrders[state.year]?.orders ?? [],
            customers: state.customers
        },
        reload: loadDashboard
    };
};

export default useDashboardData;
